import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest, TOKEN_KEY } from './api/client';
import AppLayout from './components/AppLayout';
import AnalyticsPage from './pages/AnalyticsPage';
import AppUsersPage from './pages/AppUsersPage';
import AuthPage from './pages/AuthPage';
import OverviewPage from './pages/OverviewPage';
import PlacesPage from './pages/PlacesPage';
import PlaceIssueReportsPage from './pages/PlaceIssueReportsPage';
import ReportsPage from './pages/ReportsPage';
import './App.css';

const emptyFilters = {
  search: '',
  status: '',
  isVerified: '',
  source: '',
  eventType: '',
  placeId: '',
  from: '',
  to: '',
  issueSearch: '',
  issueStatus: '',
  issueSubject: '',
  userSearch: '',
};

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const getDefaultAnalyticsDateRange = () => {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);

  return {
    from: toDateInputValue(from),
    to: toDateInputValue(to),
  };
};

const App = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [user, setUser] = useState(null);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [bootstrapUser, setBootstrapUser] = useState(null);
  const [canDevReset, setCanDevReset] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [places, setPlaces] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [reports, setReports] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [analytics, setAnalytics] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    reports: {},
  });
  const [overview, setOverview] = useState({ metrics: {}, daily: [] });
  const [placeIssueReports, setPlaceIssueReports] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [appUsers, setAppUsers] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
  });
  const [filters, setFilters] = useState(emptyFilters);
  const [authForm, setAuthForm] = useState({
    name: '',
    email: '',
    password: '',
  });
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token],
  );
  const messageType = useMemo(() => {
    if (!message) return '';
    return message.includes('נשמר') || message.includes('עודכן') || message.includes('נמחק')
      ? 'success'
      : 'error';
  }, [message]);
  const request = useCallback(
    (path, options = {}) =>
      apiRequest(path, {
        ...options,
        headers: {
          ...(token ? authHeaders : {}),
          ...(options.headers || {}),
        },
      }),
    [authHeaders, token],
  );
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await request('/bo/overview'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }, [request]);
  const loadPlaces = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        if (filters.search) params.set('search', filters.search);
        if (filters.status) params.set('status', filters.status);
        if (filters.isVerified) params.set('isVerified', filters.isVerified);
        setPlaces(await request(`/bo/places?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.isVerified, filters.search, filters.status, request],
  );
  const loadReports = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        ['source', 'placeId', 'from', 'to'].forEach(
          (key) => filters[key] && params.set(key, filters[key]),
        );
        setReports(await request(`/bo/reports?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, request],
  );
  const loadAnalytics = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
        });
        const defaultDateRange = getDefaultAnalyticsDateRange();
        params.set('from', filters.from || defaultDateRange.from);
        params.set('to', filters.to || defaultDateRange.to);
        ['source', 'eventType', 'placeId'].forEach(
          (key) => filters[key] && params.set(key, filters[key]),
        );
        setAnalytics(await request(`/bo/analytics?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, request],
  );
  const loadPlaceIssueReports = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page) });
        if (filters.issueSearch) params.set('search', filters.issueSearch);
        if (filters.issueStatus) params.set('status', filters.issueStatus);
        if (filters.issueSubject) params.set('subject', filters.issueSubject);
        setPlaceIssueReports(await request(`/bo/place-issue-reports?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.issueSearch, filters.issueStatus, filters.issueSubject, request],
  );
  const loadAppUsers = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: String(page) });
        if (filters.userSearch) params.set('search', filters.userSearch);
        setAppUsers(await request(`/bo/app-users?${params}`));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters.userSearch, request],
  );
  useEffect(() => {
    document.documentElement.setAttribute('dir', 'rtl');
    document.documentElement.setAttribute('lang', 'he');
    document.body.setAttribute('dir', 'rtl');
  }, []);
  useEffect(() => {
    if (!message) return undefined;

    const timeoutId = setTimeout(() => {
      setMessage('');
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [message]);
  useEffect(() => {
    apiRequest('/bo/auth/bootstrap-status')
      .then((data) => {
        setNeedsBootstrap(Boolean(data.needsBootstrap));
        setBootstrapUser(data.user || null);
        setCanDevReset(Boolean(data.canDevReset));
        if (data.user?.email)
          setAuthForm((current) => ({
            ...current,
            email: current.email || data.user.email,
          }));
      })
      .catch(() => setMessage('לא ניתן לבדוק סטטוס התחברות'));
  }, []);
  useEffect(() => {
    if (!token) return;
    request('/bo/me')
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      });
  }, [request, token]);
  useEffect(() => {
    if (!token || !user) return;
    if (activeTab === 'overview') loadOverview();
    if (activeTab === 'places') loadPlaces();
    if (activeTab === 'reports') loadReports();
    if (activeTab === 'analytics') loadAnalytics();
    if (activeTab === 'placeIssues') loadPlaceIssueReports();
    if (activeTab === 'appUsers') loadAppUsers();
  }, [
    activeTab,
    loadAppUsers,
    loadAnalytics,
    loadOverview,
    loadPlaceIssueReports,
    loadPlaces,
    loadReports,
    token,
    user,
  ]);
  const submitAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const path = needsBootstrap ? '/bo/auth/bootstrap' : '/bo/auth/login';
      const payload = needsBootstrap
        ? authForm
        : {
            email: authForm.email,
            password: authForm.password,
          };
      const data = await apiRequest(path, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      setNeedsBootstrap(false);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setUser(null);
  };
  const savePlace = async (placeId, payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/places/${placeId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('המקום נשמר');
      await loadPlaces(places.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const savePlaceIssueReport = async (reportId, payload) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/place-issue-reports/${reportId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setMessage('הדיווח עודכן');
      await loadPlaceIssueReports(placeIssueReports.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const deleteAppUser = async (user) => {
    const identifier = user.userId || user.anonymousId || user.deviceId || user.id;
    const approved = window.confirm(
      `למחוק את המשתמש ${identifier} וכל המידע שקשור אליו?\n\nזה ימחק דיווחים, נקודות, מועדפים, היסטוריית חיפוש, סשנים ואירועים. הפעולה לא הפיכה.`,
    );

    if (!approved) return;

    setLoading(true);
    setMessage('');
    try {
      const result = await request(`/bo/app-users/${user.id}`, {
        method: 'DELETE',
      });
      const deleted = result.deleted || {};
      setMessage(`המשתמש נמחק (${deleted.reports || 0} דיווחים)`);
      await loadAppUsers(appUsers.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const saveAppUserFullName = async (user, fullName) => {
    setLoading(true);
    setMessage('');
    try {
      await request(`/bo/app-users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ fullName }),
      });
      setMessage('השם המלא נשמר');
      await loadAppUsers(appUsers.page || 1);
    } catch (error) {
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  const renderPage = () => {
    if (activeTab === 'overview') return <OverviewPage overview={overview} />;
    if (activeTab === 'reports')
      return (
        <ReportsPage
          reports={reports}
          filters={filters}
          setFilters={setFilters}
          loadReports={loadReports}
          loading={loading}
        />
      );
    if (activeTab === 'placeIssues')
      return (
        <PlaceIssueReportsPage
          reports={placeIssueReports}
          filters={filters}
          setFilters={setFilters}
          loadReports={loadPlaceIssueReports}
          onSaveReport={savePlaceIssueReport}
          loading={loading}
        />
      );
    if (activeTab === 'analytics')
      return (
        <AnalyticsPage
          analytics={analytics}
          filters={filters}
          setFilters={setFilters}
          loadAnalytics={loadAnalytics}
          loading={loading}
        />
      );
    if (activeTab === 'appUsers')
      return (
        <AppUsersPage
          users={appUsers}
          filters={filters}
          setFilters={setFilters}
          loadUsers={loadAppUsers}
          onDeleteUser={deleteAppUser}
          onSaveUserFullName={saveAppUserFullName}
          loading={loading}
        />
      );
    return (
      <PlacesPage
        places={places}
        filters={filters}
        setFilters={setFilters}
        loadPlaces={loadPlaces}
        onSavePlace={savePlace}
        loading={loading}
      />
    );
  };
  if (!token || !user) {
    return (
      <AuthPage
        needsBootstrap={needsBootstrap}
        bootstrapUser={bootstrapUser}
        canDevReset={canDevReset}
        authForm={authForm}
        setAuthForm={setAuthForm}
        onSubmit={submitAuth}
        loading={loading}
        message={message}
      />
    );
  }

  console.log('activeTab', activeTab);
  return (
    <AppLayout activeTab={activeTab} onTabChange={setActiveTab} user={user} onLogout={logout}>
      {message && (
        <div className={`toast-message ${messageType}`} role="status" aria-live="polite">
          <span>{message}</span>
          <button type="button" aria-label="סגור הודעה" onClick={() => setMessage('')}>
            ×
          </button>
        </div>
      )}
      {renderPage()}
      {loading && <div className="loading">טוען...</div>}
    </AppLayout>
  );
};
export default App;
